import { Button, Input } from '@ibsys/kit';
import { XIcon } from 'lucide-react';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type DownloadTemplateEntityType, useUploadDownloadTemplate } from '@/entities/download-template';
import { Dropzone, IconButton, TruncatedText } from '@/shared/ui';

/** Расширения файла шаблона, которые принимает система: шаблон пишется разметкой */
const ACCEPTED_FILE_TYPES = ['.html'];

/** Предельный размер файла шаблона, МБ */
const MAX_FILE_SIZE_MB = 20;

type Props = {
  /** Сущность открытой вкладки: её шаблон и заводится */
  entityType: DownloadTemplateEntityType;
};

/** Поля формы заведения шаблона */
type UploadFormValues = {
  name: string;
  file: File | null;
};

/**
 * Заведение шаблона: наименование и файл. Оба обязательны — без наименования форма не
 * отправляется, без файла кнопка загрузки недоступна. По успеху поля очищаются, а перечень
 * сущности перезапрашивается мутацией.
 */
export const DownloadTemplateUploadForm: FC<Props> = ({ entityType }) => {
  const { t } = useTranslation(['downloadTemplates', 'common']);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const { mutate: uploadTemplate, isPending } = useUploadDownloadTemplate();

  const { control, handleSubmit, watch, setValue, reset } = useForm<UploadFormValues>({
    defaultValues: { name: '', file: null },
  });
  const file = watch('file');

  const handleFilesChange = (files: File[]) => {
    setFileErrors([]);
    setValue('file', files[0] ?? null);
  };

  const handleFileRemove = () => {
    setFileErrors([]);
    setValue('file', null);
  };

  const handleUpload = ({ name, file: selectedFile }: UploadFormValues) => {
    // Без файла шаблон не заводится: пустая форма отвечает тем же местом, где файл прикладывают
    if (!selectedFile) {
      setFileErrors([t('downloadTemplates:upload.fileRequired')]);
      return;
    }

    uploadTemplate(
      { entityType, name: name.trim(), file: selectedFile },
      {
        onSuccess: () => {
          reset();
          setFileErrors([]);
        },
      },
    );
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit(handleUpload)}>
      <Input
        control={control}
        name="name"
        label={t('downloadTemplates:upload.name')}
        placeholder={t('downloadTemplates:upload.namePlaceholder')}
        disabled={isPending}
        rules={{
          required: { value: true, message: t('common:required') },
          // Наименование из одних пробелов бэкенд не примет — отсекаем до отправки
          validate: (value) => (typeof value === 'string' && value.trim().length > 0) || t('common:required'),
        }}
      />

      {file ? (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 p-3">
          <TruncatedText className="text-sm font-medium">{file.name}</TruncatedText>
          <IconButton
            variant="ghost"
            size="icon"
            className="ml-auto shrink-0"
            label={t('downloadTemplates:upload.removeFile')}
            disabled={isPending}
            icon={<XIcon className="size-4" />}
            onClick={handleFileRemove}
          />
        </div>
      ) : (
        <Dropzone
          acceptedFileTypes={ACCEPTED_FILE_TYPES}
          maxFileSize={MAX_FILE_SIZE_MB}
          maxFiles={1}
          onFilesChange={handleFilesChange}
          onErrorsChange={setFileErrors}
          disabled={isPending}
        />
      )}

      {fileErrors.length > 0 && (
        <ul className="flex flex-col gap-1 text-sm text-destructive">
          {fileErrors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      )}

      <Button type="submit" className="self-start" isLoading={isPending}>
        {t('downloadTemplates:upload.submit')}
      </Button>
    </form>
  );
};
